import Phaser from 'phaser';
import { Damageable } from './CombatSystem';

export interface ExplosionOptions {
  x: number;
  y: number;
  radius: number;
  damage: number;
  targets: Damageable[];
  groundLayer?: Phaser.Tilemaps.TilemapLayer;
  tileSize?: number;
}

export class ExplosionSystem {
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  explode(options: ExplosionOptions): void {
    const { x, y, radius, damage, targets, groundLayer, tileSize } = options;

    // Particle burst
    this.createFireBurst(x, y, radius);
    this.createSmokePlume(x, y, radius);

    // Screen shake
    this.scene.cameras.main.shake(300, 0.02);

    // Damage all targets within radius
    for (const target of targets) {
      if (target.isDead()) continue;
      const dist = Phaser.Math.Distance.Between(x, y, target.x, target.y);
      if (dist <= radius) {
        target.takeDamage(damage);
      }
    }

    // Destroy destructible tiles if a layer is provided
    if (groundLayer && tileSize) {
      this.destroyTilesInRadius(x, y, radius, groundLayer, tileSize);
    }
  }

  /** Spawn fire/ember particle burst (orange/yellow/red, 20–30 particles) */
  private createFireBurst(x: number, y: number, radius: number): void {
    // Central bright flash
    const flash = this.scene.add.circle(x, y, radius * 0.7, 0xffffff, 1).setDepth(62);
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      scaleX: 1.5,
      scaleY: 1.5,
      duration: 180,
      ease: 'Power3',
      onComplete: () => flash.destroy(),
    });

    // Orange fireball
    const fireball = this.scene.add.circle(x, y, radius * 0.5, 0xff6600, 0.95).setDepth(61);
    this.scene.tweens.add({
      targets: fireball,
      alpha: 0,
      scaleX: 2.0,
      scaleY: 2.0,
      duration: 280,
      ease: 'Power2',
      onComplete: () => fireball.destroy(),
    });

    // Fire particles — 20–30 embers
    const particleCount = Phaser.Math.Between(20, 30);
    const fireColors = [0xff4400, 0xff8800, 0xffcc00, 0xff2200, 0xff6600];

    for (let i = 0; i < particleCount; i++) {
      const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);
      const speed = Phaser.Math.FloatBetween(radius * 0.5, radius * 2.5);
      const color = fireColors[Phaser.Math.Between(0, fireColors.length - 1)];
      const particleScale = Phaser.Math.FloatBetween(0.5, 2.0);
      const size = 4 * particleScale;
      const lifespan = Phaser.Math.Between(350, 600);

      const particle = this.scene.add
        .circle(x, y, size, color, 1)
        .setDepth(63);

      // Gravity effect: add downward drift
      const gravityDrift = radius * Phaser.Math.FloatBetween(0.3, 0.8);

      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * speed,
        y: y + Math.sin(angle) * speed + gravityDrift,
        alpha: 0,
        scaleX: 0.1,
        scaleY: 0.1,
        duration: lifespan,
        ease: 'Power2',
        onComplete: () => particle.destroy(),
      });
    }
  }

  /** Smoke follow-up: grey/white particles rising slowly */
  private createSmokePlume(x: number, y: number, radius: number): void {
    // Smoke ring expand
    const smokeRing = this.scene.add.circle(x, y, radius * 0.45, 0x444444, 0.55).setDepth(59);
    this.scene.tweens.add({
      targets: smokeRing,
      alpha: 0,
      scaleX: 2.8,
      scaleY: 2.8,
      duration: 700,
      ease: 'Power1',
      onComplete: () => smokeRing.destroy(),
    });

    // Smoke particles rising (grey/white, lifespan 1200ms)
    const smokeCount = Phaser.Math.Between(8, 12);
    const smokeColors = [0x777777, 0x999999, 0xaaaaaa, 0xcccccc, 0xdddddd];

    for (let i = 0; i < smokeCount; i++) {
      const offsetX = Phaser.Math.FloatBetween(-radius * 0.4, radius * 0.4);
      const color = smokeColors[Phaser.Math.Between(0, smokeColors.length - 1)];
      const size = Phaser.Math.FloatBetween(6, 14);
      const delay = Phaser.Math.Between(100, 400);

      const smoke = this.scene.add
        .circle(x + offsetX, y, size, color, 0.5)
        .setDepth(58);

      this.scene.time.delayedCall(delay, () => {
        if (!smoke.active) return;
        this.scene.tweens.add({
          targets: smoke,
          x: x + offsetX + Phaser.Math.FloatBetween(-20, 20),
          y: y - Phaser.Math.FloatBetween(30, 70),
          alpha: 0,
          scaleX: Phaser.Math.FloatBetween(1.5, 2.5),
          scaleY: Phaser.Math.FloatBetween(1.5, 2.5),
          duration: 1200,
          ease: 'Power1',
          onComplete: () => smoke.destroy(),
        });
      });
    }
  }

  /**
   * Spawn debris particles at a destroyed tile location.
   * Called by ExplosionSystem.destroyTilesInRadius when a destructible tile is removed.
   */
  spawnDebrisAtTile(worldX: number, worldY: number): void {
    const debrisColors = [0x8B4513, 0x6B3A10, 0x888888, 0x666666, 0xA0522D];
    const count = Phaser.Math.Between(6, 10);

    for (let i = 0; i < count; i++) {
      const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);
      const speed = Phaser.Math.FloatBetween(20, 60);
      const color = debrisColors[Phaser.Math.Between(0, debrisColors.length - 1)];

      const debris = this.scene.add
        .rectangle(worldX, worldY, Phaser.Math.Between(3, 7), Phaser.Math.Between(3, 7), color)
        .setDepth(55);

      this.scene.tweens.add({
        targets: debris,
        x: worldX + Math.cos(angle) * speed,
        y: worldY + Math.sin(angle) * speed + Phaser.Math.FloatBetween(10, 30),
        alpha: 0,
        angle: Phaser.Math.Between(90, 360),
        duration: Phaser.Math.Between(400, 700),
        ease: 'Power2',
        onComplete: () => debris.destroy(),
      });
    }
  }

  /** Blood/impact splat — small red dots at hit position */
  spawnBloodSplat(x: number, y: number): void {
    const count = Phaser.Math.Between(2, 4);
    for (let i = 0; i < count; i++) {
      const offsetX = Phaser.Math.FloatBetween(-8, 8);
      const offsetY = Phaser.Math.FloatBetween(-8, 8);
      const size = Phaser.Math.FloatBetween(2, 4);

      const dot = this.scene.add
        .circle(x + offsetX, y + offsetY, size, 0xcc0000, 0.85)
        .setDepth(56);

      this.scene.tweens.add({
        targets: dot,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.3,
        duration: 400,
        ease: 'Power2',
        delay: Phaser.Math.Between(0, 100),
        onComplete: () => dot.destroy(),
      });
    }
  }

  /**
   * Destroy (blank) destructible tiles within explosion radius.
   * Tiles with index 3 (or with `destructible` property) are removed.
   */
  destroyTilesInRadius(
    x: number,
    y: number,
    radius: number,
    groundLayer: Phaser.Tilemaps.TilemapLayer,
    tileSize: number
  ): void {
    const tileRadius = Math.ceil(radius / tileSize);
    const centerTileX = Math.floor(x / tileSize);
    const centerTileY = Math.floor(y / tileSize);

    for (let dy = -tileRadius; dy <= tileRadius; dy++) {
      for (let dx = -tileRadius; dx <= tileRadius; dx++) {
        const tx = centerTileX + dx;
        const ty = centerTileY + dy;
        const tile = groundLayer.getTileAt(tx, ty);
        if (!tile) continue;

        const tileWorldX = tx * tileSize + tileSize / 2;
        const tileWorldY = ty * tileSize + tileSize / 2;
        const dist = Phaser.Math.Distance.Between(x, y, tileWorldX, tileWorldY);
        if (dist > radius) continue;

        // Destroy tile index 3 (destructible wall)
        const isDestructible =
          tile.index === 3 ||
          (tile.properties && tile.properties['destructible'] === true);

        if (isDestructible) {
          // Spawn debris before removing the tile
          this.spawnDebrisAtTile(tileWorldX, tileWorldY);

          groundLayer.putTileAt(1, tx, ty); // replace with floor tile
          // Update collision — tile index 1 is not a wall
          const newTile = groundLayer.getTileAt(tx, ty);
          if (newTile) {
            newTile.setCollision(false);
          }
        }
      }
    }
  }
}
